import { useCallback, useRef } from "react";
import { FlightAlertsService } from "../../../api/flightAlerts.service";

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (cb: () => void) => void;
        execute: (siteKey: string, opts: { action: string }) => Promise<string>;
      };
    };
  }
}

let scriptLoadPromise: Promise<void> | null = null;

const loadRecaptchaScript = (siteKey: string): Promise<void> => {
  if (window.grecaptcha?.enterprise) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error("Failed to load reCAPTCHA script"));
    };
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
};

export interface UseRecaptchaResult {
  /**
   * Generates a fresh, single-use reCAPTCHA Enterprise token for `action`. Tokens are short-lived
   * (~2 min) — call this again right before each real request rather than reusing one across
   * steps of the wizard.
   */
  getToken: (action: string) => Promise<string>;
}

/**
 * Loads the real Google reCAPTCHA Enterprise script (site key fetched live from
 * GET /v2/auth/recaptcha-site-key, so nothing is hardcoded here) and exposes a way to mint
 * tokens for the backend's SCORE-mode bot check (RecaptchaMode.SCORE, the default `/lookup` uses).
 */
export const useRecaptcha = (): UseRecaptchaResult => {
  const siteKeyRef = useRef<string | null>(null);

  const getToken = useCallback(async (action: string): Promise<string> => {
    if (!siteKeyRef.current) {
      const service = new FlightAlertsService();
      const { data } = await service.getRecaptchaSiteKey();
      if (!data.enabled || !data.siteKeys.web) {
        throw new Error("reCAPTCHA is not enabled, or no web site key is configured");
      }
      siteKeyRef.current = data.siteKeys.web;
    }
    const siteKey = siteKeyRef.current;

    await loadRecaptchaScript(siteKey);

    return new Promise<string>((resolve, reject) => {
      window.grecaptcha!.enterprise.ready(() => {
        window
          .grecaptcha!.enterprise.execute(siteKey, { action })
          .then(resolve)
          .catch(reject);
      });
    });
  }, []);

  return { getToken };
};
